import { useCallback, useEffect, useRef, useState } from "react";
import errorToast from "../../../helper-components/error-toast";
import { loadAllTrays } from "./load-all-trays";
import { errorMessage } from "./tray-list-state";
import {
  deselectTray,
  deselectTrays,
  loadedSelectionState,
  refreshSelectedTray,
  selectTrays,
  selectionCount,
  toggleTraySelection,
  traysToPrint,
} from "./tray-selection";

const LOAD_ALL_FAILED = "Failed to load all trays";

// A refused page throws a plain Error with the server's message; a failed
// request is an axios error carrying it in response.data.message.
const loadAllError = (err) =>
  err?.response ? errorMessage(err, LOAD_ALL_FAILED) : err?.message || LOAD_ALL_FAILED;

/**
 * Trays picked for a bulk label print on the Active tab. The selection
 * survives scrolling and searching (pick some, search, pick more) and is
 * cleared when the tab changes. `selectAll` loads every tray matching the
 * current search, since only the first pages are on screen.
 */
const useTraySelection = ({ status, searchValue, trays, totalCount }) => {
  const [selected, setSelected] = useState({});
  const [selectingAll, setSelectingAll] = useState(false);
  // Bumped on every tab change, so a "select all" still loading for the old
  // tab can't fill the new tab's selection.
  const generationRef = useRef(0);
  // Trays inactivated while a "select all" loads; its result predates that.
  const droppedRef = useRef(new Set());

  useEffect(() => {
    generationRef.current += 1;
    setSelected({});
    setSelectingAll(false);
  }, [status]);

  const toggle = useCallback((tray) => setSelected((s) => toggleTraySelection(s, tray)), []);

  // Header checkbox: clears the trays on screen when all are ticked, else ticks them all.
  const toggleLoaded = () =>
    setSelected((s) =>
      loadedSelectionState(s, trays) === "all" ? deselectTrays(s, trays) : selectTrays(s, trays)
    );

  const selectAll = () => {
    const generation = generationRef.current;
    droppedRef.current = new Set();
    setSelectingAll(true);
    loadAllTrays({ status, searchValue })
      .then((all) => {
        if (generation !== generationRef.current) return;
        const dropped = droppedRef.current;
        setSelected((s) => selectTrays(s, all.filter((tray) => !dropped.has(tray.tray_id))));
      })
      .catch((err) => {
        if (generation === generationRef.current) errorToast(loadAllError(err));
      })
      .finally(() => {
        if (generation === generationRef.current) setSelectingAll(false);
      });
  };

  const loadedState = loadedSelectionState(selected, trays);

  return {
    selected,
    count: selectionCount(selected),
    loadedState,
    // Every tray of this tab and search is ticked, loaded or not.
    allMatchingSelected: loadedState === "all" && trays.length >= totalCount,
    selectingAll,
    toggle,
    toggleLoaded,
    selectAll,
    clear: () => setSelected({}),
    refresh: (saved) => setSelected((s) => refreshSelectedTray(s, saved)),
    drop: (trayId) => {
      droppedRef.current = new Set([...droppedRef.current, trayId]);
      setSelected((s) => deselectTray(s, trayId));
    },
    toPrint: () => traysToPrint(selected),
  };
};

export default useTraySelection;
